import { useEffect, useSyncExternalStore, type ReactNode } from 'react'
import { messages, type MessageKey } from './messages'
export type Locale = 'zh-CN' | 'en-US'
export const LOCALE_STORAGE_KEY = 'hivecloud:user-locale'

export function normalizeLocale(value: unknown): Locale | undefined {
  if (value === 'zh-CN' || value === 'zh' || value === 'zh-Hans') return 'zh-CN'
  if (value === 'en-US' || value === 'en') return 'en-US'
  return undefined
}
function initialLocale(): Locale {
  if (typeof window === 'undefined') return 'zh-CN'
  const query = normalizeLocale(new URLSearchParams(window.location.search).get('locale'))
  if (query) return query
  try { const stored = normalizeLocale(localStorage.getItem(LOCALE_STORAGE_KEY)); if (stored) return stored } catch { /* preference remains in memory */ }
  for (const language of navigator.languages ?? [navigator.language]) {
    const locale = normalizeLocale(language?.startsWith('en') ? 'en' : language?.startsWith('zh') ? 'zh' : undefined)
    if (locale) return locale
  }
  return 'zh-CN'
}
let locale = initialLocale()
const listeners = new Set<() => void>()
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener) } }
export function setLocale(value: Locale) {
  locale = value
  if (typeof document !== 'undefined') document.documentElement.lang = value
  if (typeof window !== 'undefined') {
    try { localStorage.setItem(LOCALE_STORAGE_KEY, value) } catch { /* keep the active locale */ }
    const url = new URL(window.location.href)
    if (url.searchParams.has('locale')) {
      url.searchParams.set('locale', value)
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
    }
  }
  listeners.forEach(listener => listener())
}
export function I18nProvider({ children }: { children: ReactNode }) {
  useEffect(() => { setLocale(locale) }, [])
  return children
}
export function useI18n() {
  const current = useSyncExternalStore(subscribe, () => locale, () => 'zh-CN' as Locale)
  const t = (key: MessageKey, params?: Record<string, string | number>) => {
    const local = messages[current] as Record<string, string>
    const fallback = messages['zh-CN'] as Record<string, string>
    let value = local[key] || fallback[key] || key
    for (const [name, replacement] of Object.entries(params ?? {})) value = value.replaceAll(`{${name}}`, String(replacement))
    return value
  }
  return { locale: current, t, setLocale, toggleLocale: () => setLocale(current === 'zh-CN' ? 'en-US' : 'zh-CN') }
}
